import prettyBytes from 'pretty-bytes'
import moment from 'moment'
import { LocalTrack, RemoteTrack, Room } from 'livekit-client'
import SimplePeer from 'simple-peer'
import semver from 'semver'
import stringify from 'json-stringify-safe'
import ElectronIPC from './services/ElectronIPC'
import { serializeError } from 'serialize-error'
import { env } from './services/EnvironmentVariables'
import path from 'path'

export function copyStyles(sourceDoc: any, targetDoc: any) {
    Array.from(sourceDoc.querySelectorAll('style')).forEach((style: any) => {
        targetDoc.head.appendChild(style.cloneNode(true))
    })

    const assetPath = getAssetPath()
    Array.from(sourceDoc.querySelectorAll('link[rel="stylesheet"]')).forEach((link: any) => {
        const clonedLink = link.cloneNode(true)
        let relativeHref = link.getAttribute('href')
        if (relativeHref.startsWith('/')) {
            relativeHref = relativeHref.slice(1)
        }
        clonedLink.href = new URL(relativeHref, 'file://' + assetPath + '/').href
        targetDoc.head.appendChild(clonedLink)
    })
}

export const arrayInPlaceRemove = function <T>(arr: T[], callback: (v: T, i: number) => boolean) {
    let i = arr.length
    while (i--) {
        if (callback(arr[i], i)) {
            arr.splice(i, 1)
        }
    }
}

export function removeInPlace<T>(array: Array<T>, condition: (value: T) => boolean) {
    let next_place = 0

    for (let value of array) {
        if (!condition(value)) array[next_place++] = value
    }

    array.splice(next_place)
}

export function formatBytes(
    value: string | number | null | undefined,
    options?: { fallbackString?: string; multiplier?: number }
) {
    if (!options?.fallbackString) {
        var fallbackString = '-'
    } else {
        var fallbackString = options.fallbackString
    }

    if (typeof value === 'string') {
        let parsed = parseInt(value)
        if (isNaN(parsed)) {
            return fallbackString
        } else {
            if (options?.multiplier) {
                parsed *= options.multiplier
            }
            return prettyBytes(parsed)
        }
    } else if (typeof value === 'number') {
        if (isNaN(value)) {
            return fallbackString
        } else {
            if (options?.multiplier) {
                value *= options.multiplier
            }
            return prettyBytes(value)
        }
    } else {
        return fallbackString
    }
}

export function passthroughNumber(value: any): number | null {
    if (typeof value === 'string') {
        let parsed = parseInt(value)
        if (isNaN(parsed)) {
            return null
        } else {
            return parsed
        }
    } else if (typeof value === 'number') {
        if (isNaN(value)) {
            return null
        } else {
            return value
        }
    } else {
        return null
    }
}

export const sleep = (ms: number) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            // @ts-ignore
            resolve()
        }, ms)
    })
}

export const formatTimestamp = (timestamp: Date, options?: { concise?: boolean }): string => {
    const mts = moment(timestamp)

    if (moment().isSame(mts, 'day')) {
        return moment(timestamp).format('hh:mma')
    } else if (moment().isSame(mts, 'year')) {
        return moment(timestamp).format(options?.concise ? 'MM/DD' : 'MM/DD hh:mma')
    } else {
        return moment(timestamp).format(options?.concise ? 'MM/DD/YY' : 'YYYY/MM/DD hh:mma')
    }
}

export const formatSeconds = (seconds: number) => {
    let s = seconds % 60
    let m = Math.floor(seconds / 60)
    return `${m}:${s < 10 ? '0' : ''}${s}`
}

/**
 * For each key in object a, copy the corresponding field in object b.
 */
export const copyObjectByKeyOnto = (a: any, b: any) => {
    Object.keys(a).forEach((k) => {
        // @ts-ignore
        if (b && b[k] != null) {
            // @ts-ignore
            a[k] = b[k]
        }
    })

    return a
}

export function extractErrorMessage(e: unknown) {
    if (e instanceof Error) {
        return e.message || ''
    } else {
        return String(e)
    }
}

export function arrayDiff<A, B>(as: A[], bs: B[], equal?: (a: A, b: B) => boolean) {
    let added = []
    let removed = []

    // if a in as is not in bs, then 'added'.
    for (let a of as) {
        if (
            bs.filter((b) => {
                if (equal) {
                    return equal(a, b)
                } else {
                    // @ts-ignore
                    return a === b
                }
            }).length === 0
        ) {
            added.push(a)
        }
    }

    // if b in bs is not in as, then 'removed'.
    for (let b of bs) {
        if (
            as.filter((a) => {
                if (equal) {
                    return equal(a, b)
                } else {
                    // @ts-ignore
                    return a === b
                }
            }).length === 0
        ) {
            removed.push(b)
        }
    }

    return { added, removed }
}

export function smartStringify(obj: any) {
    if (obj === null || typeof obj !== 'object') {
        if (typeof obj === 'function') {
            return '<function>'
        } else {
            return obj
        }
    } else {
        if (obj instanceof MediaStream) {
            return `<MediaStream id='${obj.id}'>`
        } else if (obj instanceof MediaStreamTrack) {
            return `<MediaStreamTrack id='${obj.id}' label='${obj.label}'>`
        } else if (obj instanceof RemoteTrack || obj instanceof LocalTrack) {
            return `<MediaStreamTrack mediaStreamTrack.id='${obj.mediaStreamTrack.id}'>`
        } else if (obj instanceof SimplePeer) {
            return `<SimplePeer>`
        } else if (obj instanceof Room) {
            return `<LiveKit:Room sid=${obj.getSid()}>`
        } else {
            let acc: any = {}
            Object.keys(obj).forEach((key) => {
                acc[key] = smartStringify(obj[key])
            })
            return acc
        }
    }
}

export function isNullMediaDeviceId(id: string) {
    const isNotNull = typeof id === 'string' && !!id && id !== '_none' && id !== '_undef'
    return !isNotNull
}

/**
 * @description strip decorators from version string to be compatible with semver.
 * e.g. convert 1.5.0rc1 to 1.5.0. we incrementally remove characters from the end
 * of the string provided until we reach a valid semver.
 *
 * @param ver
 * @returns
 */
export function stripSemverVersion(ver: string) {
    // discard dashes (e.g. 2.0.0-preview)
    ver = ver.split('-')[0]

    while (ver.length > 0) {
        if (semver.valid(ver)) {
            return ver
        }
        ver = ver.slice(0, -1)
    }

    return null
}

export async function getProcessArchitecture() {
    try {
        // @ts-ignore
        const a = await globalThis.navigator?.userAgentData?.getHighEntropyValues(['architecture'])
        return a.architecture || ''
    } catch (e) {
        return ''
    }
}

export async function checkDirectoryWithFallback(directoryToCheck: string, fallbackDirectories: string[]) {
    // success case:
    const isWritable = (await ElectronIPC.checkDirectory(directoryToCheck, { checkWritable: true }))?.success === true
    if (isWritable) {
        return { directory: directoryToCheck, isFallbackDirectory: false }
    }

    // otherwise, try fallback directories.
    for (let fallbackDirectory of fallbackDirectories) {
        const isWritable =
            (await ElectronIPC.checkDirectory(fallbackDirectory, { checkWritable: true, createIfNotExists: true }))
                ?.success === true
        if (isWritable) {
            return { directory: fallbackDirectory, isFallbackDirectory: true }
        }
    }

    // if none of the directories work:
    return { directory: null }
}

export function openAudioMIDISetup() {
    ElectronIPC.open({ command: '/System/Applications/Utilities/Audio MIDI Setup.app' })
}

export function setWithDefault<T>(value: T, _default: T) {
    return value == null ? _default : value
}

export function formatObjectNotation(...args: any) {
    let baseString = args[0]
    let index = 0

    if (baseString instanceof Error) {
        return stringify(serializeError(baseString))
    } else if (typeof baseString === 'string') {
        return baseString.replace(/%O/g, function () {
            index++
            try {
                return stringify(args[index])
            } catch (e) {
                if (e instanceof Error) {
                    return `<stringify error (message="${e.message}")>`
                } else {
                    return `<stringify error>`
                }
            }
        })
    } else {
        try {
            return stringify(baseString)
        } catch (e) {
            if (e instanceof Error) {
                return `<stringify error (message="${e.message}")>`
            } else {
                return `<stringify error>`
            }
        }
    }
}

export function extractVideoTrack(stream: any): MediaStreamTrack | null {
    if (stream instanceof MediaStream) {
        return stream.getVideoTracks()[0] || null
    } else {
        return null
    }
}

export function toFixed1WithoutTrailing0(n: number) {
    const fixed = n.toFixed(1)
    return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed
}

export function getCurrentIanaTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (e) {
        return ''
    }
}

export function copyToClipboard(value: string): Promise<void> {
    return new Promise((resolve) => {
        // Primary way of copying text
        navigator.clipboard
            .writeText(value)
            .then(() => {})
            .catch((e) => {
                ElectronIPC.copyToClipboard(value)
            })
            .finally(() => {
                resolve()
            })
    })
}

export function chunkArray<T>(array: T[], n: number): T[][] {
    const chunkedArray: T[][] = []

    for (let i = 0; i < array.length; i += n) {
        chunkedArray.push(array.slice(i, i + n))
    }

    return chunkedArray
}

export function isSubset<T>(A: T[], B: T[]): boolean {
    return A.every((element) => B.includes(element))
}

export function fillSparseArray<T>(sparseArray: T[], defaultValue: T) {
    let fullArray = new Array(sparseArray.length)
    for (let i = 0; i < fullArray.length; ++i) {
        fullArray[i] = sparseArray[i] ?? defaultValue
    }
    return fullArray
}

export function getAssetPath(): string {
    if (import.meta.env.IS_ELECTRON_PACKAGED) {
        if (!env.ELECTRON_APP_PATH) {
            throw new Error('env.ELECTRON_APP_PATH is not defined')
        }
        return path.join(env.ELECTRON_APP_PATH, 'build')
    }

    return '/'
}

export function detectBrowerOs() {
    // @ts-ignore
    const platform = globalThis.navigator?.userAgentData?.platform ?? ''
    if (platform === 'Windows') {
        return 'win32'
    } else if (platform === 'macOS') {
        return 'darwin'
    }
    // otherwise, fall through and use backup method

    const userAgent = window.navigator.userAgent.toLowerCase()
    if (userAgent.includes('win')) {
        return 'win32'
    } else if (userAgent.includes('mac')) {
        return 'darwin'
    } else if (userAgent.includes('linux')) {
        return 'linux'
    } else {
        return 'unknown'
    }
}

export function numberToColumnLabel(n: number): string {
    if (n < 1) {
        n = 1
    }

    let result = ''
    while (n > 0) {
        const remainder = (n - 1) % 26 // Get the remainder (0-25)
        result = String.fromCharCode(65 + remainder) + result // Convert to A-Z
        n = Math.floor((n - 1) / 26) // Update n for the next iteration
    }
    return result
}

/**
 * Returns the pathname of the window location, removing the drive letter
 * if it exists, especially on Windows.
 *
 * @returns The pathname of the window location.
 */
export function getWindowLocationPathname() {
    if (window.location.protocol === 'file:') {
        // For file:// URLs, manually parse
        let pathname = window.location.pathname
        // Remove drive letter
        pathname = pathname.replace(/^\/[A-Za-z]:/, '')
        return pathname || '/'
    }
    return window.location.pathname
}

export function truncateString(str: string, maxLength: number) {
    if (str.length <= maxLength) {
        return str
    }
    return str.slice(0, maxLength) + '...'
}

const guestUsernameRegex = /^guest\d+\d$/
export function isGuestUsername(username: string) {
    return guestUsernameRegex.test(username || '')
}

export function carmackClick(fn: () => void) {
    return (event: React.MouseEvent<any>) => {
        if (event.button === 0) {
            fn()
        }
    }
}
